import InputField from '@allesauto/core/inputField';
import Textarea from '@allesauto/core/textarea';
import { ContactType, PaymentType, SchemaTypes } from '@allesauto/graphql';
import { CashOption, InputMaybe } from '@allesauto/graphql/src/mock/schema';
import translate from '@allesauto/i18n';
import { pushAnalyticsEvent } from '@allesauto/utils/analytics';
import { useFormik } from 'formik';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import OverlaySuccess from '@/components/forms/overlaySuccess/OverlaySuccess';
import PrivacyNotice from '@/components/forms/PrivacyNotice/PrivacyNotice';
import Footer from '@/components/OverlayFormFooter/OverlayFormFooter';
import { CustomerDetails } from '@/components/PDP/aboveFold/carDealerDetails/CarDealerDetails';
import ReCaptchaValidator, {
  ITokenValidator,
} from '@/components/ReCaptcha/ReCaptchaValidator';

import { useCarConfiguratorQueryParams } from '@/features/PDP/hooks';
import { useRequestVehicleMutation } from '@/features/whitelabel/hooks/useRequestVehicleMutation';
import {
  sharedOverlayDetailsSteps,
  sharedOverlaySuccessTranslations,
} from '@/global/funcs/overlaySuccessSharedTranslations';
import useNewsletterSubscribeCheckbox from '@/global/hooks/useNewsletterSubscribeCheckbox';
import { RootState } from '@/store/redux';
import {
  closeOverlay,
  openOverlay,
  setOverlayLoadingState,
} from '@/store/redux/actions/app';

import useTranslationOrderVehicleForm from './orderVehicleFormTranslation';
import {
  allowedPhonePattern,
  phoneInputChange,
  phoneOnPasteSanitizer,
} from '../validation/formUtils';

export interface OrderVehicleFormProps {
  dealerId: string;
  vehicleId: string;
  carModel?: string;
  carManufacturer?: string;
  dealerName: string;
  contactPerson?: SchemaTypes.ContactPerson;
  financeCalculationResult?: SchemaTypes.FinanceCalculationResultInput;
  paymentType: SchemaTypes.PaymentType | null;
  onBackToSearchClick: () => void;
  customerDetails?: CustomerDetails;
  vehiclePrice: number;
}

const OrderVehicleForm: FC<React.PropsWithChildren<OrderVehicleFormProps>> = ({
  carModel,
  carManufacturer,
  dealerId,
  dealerName,
  contactPerson,
  onBackToSearchClick,
  vehicleId,
  financeCalculationResult,
  paymentType,
  customerDetails,
  vehiclePrice,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { requestVehicle } = useRequestVehicleMutation();
  const { carConfiguratorId } = useCarConfiguratorQueryParams();
  const { email, firstName, lastName, phone } = customerDetails ?? {};
  const {
    memoizedOrderVehicleContactFormSchema,
    memoizedOrderVehicleFormTranslations,
  } = useTranslationOrderVehicleForm();
  const overlayLoadingState = useSelector(
    (state: RootState) => state.app.overlayProps.isLoading
  );
  const userMessagePrefilled = useMemo(() => {
    if (carConfiguratorId) {
      return translate(
        intl,
        'ui.FORMS.CONTACT_DEALER.USER_MSG_PREFILLED_CONFIGUREDCARS',
        {
          car: `${carManufacturer} ${carModel}`,
        }
      );
    } else {
      return translate(intl, 'ui.FORMS.CONTACT_DEALER.USER_MSG_PREFILLED', {
        car: `${carManufacturer} ${carModel}`,
      });
    }
  }, [carConfiguratorId, carManufacturer, carModel, intl]);

  const [cashOption, setCashOption] = useState<
    InputMaybe<CashOption> | undefined
  >();

  useEffect(() => {
    if (paymentType === PaymentType.CashRange) {
      setCashOption(CashOption.Prepayment);
    } else {
      setCashOption(undefined);
    }
  }, [paymentType]);

  const tokenValidator = useRef<ITokenValidator | undefined>();

  const setTokenValidator = useCallback((v: ITokenValidator) => {
    tokenValidator.current = v;
  }, []);

  const {
    NewsletterSubscribeCheckbox,
    checkboxProps,
    subscribe: subscribeToNewsletter,
  } = useNewsletterSubscribeCheckbox({ isDarkBg: false });

  const OVERLAY_SUCCESS_TRANSLATIONS = sharedOverlaySuccessTranslations(
    intl,
    dealerName
  );

  const OVERLAY_SUCCESS_DETAIL_STEPS: React.ReactNode[] | undefined =
    (contactPerson &&
      sharedOverlayDetailsSteps(intl, dealerName, contactPerson)) ||
    undefined;

  const formik = useFormik({
    initialValues: {
      userMsg: userMessagePrefilled,
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      email: email ?? '',
      newsletterAgreement: false,
      phone: phone ?? '',
    },
    onSubmit: async (data) => {
      if (process.env.NEXT_PUBLIC_FEATUREFLAG_RECAPTCHA) {
        const token = await tokenValidator.current?.validate();
        if (!token) {
          return false;
        }
      }

      dispatch(setOverlayLoadingState(true));

      const formData: SchemaTypes.RequestVehicleInput = {
        contactMessage: data.userMsg || userMessagePrefilled,
        userFirstName: data.firstName,
        userLastName: data.lastName,
        userEmail: data.email,
        userPhone: data.phone,
        newsletterAgreement: data.newsletterAgreement,
        ...(financeCalculationResult &&
          financeCalculationResult.monthlyInstallment && {
            financeCalculationResult,
          }),
        paymentType,
        contactType: ContactType.OrderVehicleRequest,
        vehiclePrice,
      };

      if (cashOption) {
        formData.cashOption = cashOption;
      }

      try {
        await requestVehicle(vehicleId, dealerId, formData, carConfiguratorId);
        subscribeToNewsletter({
          email: formData.userEmail,
          source: 'SubscribeContactDealerCommon',
        });
        // dataLayer push
        pushAnalyticsEvent({
          event: 'contact',
          form_process: 'contact_formFinish',
          product_name: `${carManufacturer} ${carModel}`,
          product_id: vehicleId,
        });

        dispatch(closeOverlay());

        setTimeout(
          () =>
            dispatch(
              openOverlay({
                children: (
                  <OverlaySuccess
                    bannerHeadline={
                      OVERLAY_SUCCESS_TRANSLATIONS.SUBMISSION_THANK_YOU
                    }
                    bannerDescription={
                      OVERLAY_SUCCESS_TRANSLATIONS.DEALER_CONFIRMATION_MSG
                    }
                    detailsHeadline={
                      OVERLAY_SUCCESS_TRANSLATIONS.NEXT_STEPS_TEXT
                    }
                    details={OVERLAY_SUCCESS_DETAIL_STEPS}
                    ctaLabel={OVERLAY_SUCCESS_TRANSLATIONS.BUTTON_TEXT}
                    onBtnClick={onBackToSearchClick}
                  />
                ),
                title: '',
                withPaddings: false,
              })
            ),
          100
        );
      } catch (e) {
        // the error is handled by the apollo errorHandler
        dispatch(setOverlayLoadingState(false));
      }
    },

    validationSchema: memoizedOrderVehicleContactFormSchema,
  });

  const className = {
    personalDetailsContainer: 'mb-[60px]',
    requestLabel: 'font-weight-bold text-grey100',
    newsletterPrivacy: 'mt-60px',
    newsletterText:
      'font-regular font-weight-normal mt-20px text-p3 text-grey100',
    dateTimeWindowContainer:
      'mt-30px grid grid-cols-1 md:flex lg:flex xl:flex ',
    timeframeWindowContainer:
      'mt-30px md:flex-1 md:ml-40px md:mt-0 lg:flex-1 lg:ml-40px lg:mt-0 xl:flex-1 xl:ml-40px xl:mt-0 z-1',
    requiredFieldNote: 'font-regular mt-20px pb-10px text-p3 text-grey100',
    error: 'text-red60 text-p3 mt-10px',
  };

  return (
    <form className="mx-[3px] pb-100px">
      <div className="h-full mb-20px">
        <div className={className.personalDetailsContainer}>
          <Textarea
            label={memoizedOrderVehicleFormTranslations.USER_MSG_LABEL}
            className="mt-20px"
            name="userMsg"
            value={formik.values.userMsg}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.errors.userMsg ? (
            <div className={className.error}>{formik.errors.userMsg}</div>
          ) : (
            ''
          )}
          <InputField
            placeholder={`${memoizedOrderVehicleFormTranslations.FIRST_NAME}*`}
            errorMessage={
              formik.touched.firstName ? formik.errors.firstName : ''
            }
            name="firstName"
            customStyle="mt-30px"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          <InputField
            placeholder={`${memoizedOrderVehicleFormTranslations.LAST_NAME}*`}
            errorMessage={formik.touched.lastName ? formik.errors.lastName : ''}
            customStyle="mt-30px"
            name="lastName"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />

          <InputField
            type="email"
            errorMessage={formik.touched.email ? formik.errors.email : ''}
            placeholder={`${memoizedOrderVehicleFormTranslations.EMAIL_LABEL}*`}
            customStyle="mt-30px text-purple80"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />

          <InputField
            type="tel"
            errorMessage={formik.touched.phone ? formik.errors.phone : ''}
            label=""
            placeholder={`${memoizedOrderVehicleFormTranslations.PHONE}*`}
            customStyle="mt-30px"
            name="phone"
            value={formik.values.phone}
            onChange={(event: React.FormEvent) =>
              formik.setFieldValue('phone', phoneInputChange(event))
            }
            onPaste={(event: React.ClipboardEvent) =>
              formik.setFieldValue('phone', phoneOnPasteSanitizer(event))
            }
            onKeyPress={allowedPhonePattern}
            onBlur={formik.handleBlur}
          />
        </div>
        <div className={className.newsletterPrivacy}>
          <NewsletterSubscribeCheckbox {...checkboxProps} />
          <PrivacyNotice
            translationKey="ui.FORMS.COMMON.PRIVACY_NOTICE.CONTACT_DEALER"
            className={className.newsletterText}
          />
        </div>
        <div className={className.requiredFieldNote}>
          {memoizedOrderVehicleFormTranslations.REQUIRED_FIELD_NOTE}
        </div>
      </div>

      {process.env.NEXT_PUBLIC_FEATUREFLAG_RECAPTCHA && (
        <ReCaptchaValidator setStepValidator={setTokenValidator} />
      )}

      <Footer
        buttonText={memoizedOrderVehicleFormTranslations.BUTTON_TEXT}
        onButtonClick={formik.handleSubmit}
        isButtonDisabled={formik.isSubmitting}
        className="!z-50"
        showButtonLoader={overlayLoadingState || false}
      />
    </form>
  );
};

export default OrderVehicleForm;
